import ApiService from "./ApiService";
import { WEB_APP_CODE } from "./PayPlanUtil";

function failureMessage(data, fallback) {
    if (data?.code === "FAILURE") {
        return data.message || fallback;
    }
    return null;
}

/**
 * Web puzzle-coin spend (hints and other in-play features).
 * POST /home/puzzle-coins/spend — always {@code appCode=web_app}.
 * Color-basket unlock: POST /home/jigsaw/{id}/color-baskets/unlock (puzzle coins, not credits).
 */
const HomePuzzleCoinsService = {
    WEB_APP_CODE,

    /**
     * @param {{ featureUsed?: string, coins?: number, appCode?: string }} opts
     * @returns {Promise<{ featureUsed: string, coinsSpent: number, totalPuzzleCoins: number, appCode: string }>}
     */
    async spendForHint({ featureUsed = "puzzle_hint", coins = 1, appCode = WEB_APP_CODE } = {}) {
        const data = await ApiService.postData("/home/puzzle-coins/spend", {
            appCode,
            featureUsed,
            coins,
        });
        const err = failureMessage(data, "Failed to spend puzzle coins");
        if (err) throw new Error(err);
        return {
            featureUsed: data?.featureUsed || featureUsed,
            coinsSpent: Number(data?.coinsSpent) || coins,
            totalPuzzleCoins: Number(data?.totalPuzzleCoins) || 0,
            appCode: data?.appCode || appCode,
        };
    },

    /**
     * Unlock one color basket with puzzle coins (UC-02).
     * Idempotent once per (user, jigsaw, basketCount, basketIndex). Cost = unlockCoinCost (default 5).
     * @param {{ jigsawId: string, basketCount: number, basketIndex: number, appCode?: string }} opts
     */
    async unlockColorBasket({
        jigsawId,
        basketCount,
        basketIndex,
        appCode = WEB_APP_CODE,
    } = {}) {
        if (!jigsawId) {
            throw new Error("jigsawId is required");
        }
        if (basketCount == null) {
            throw new Error("basketCount is required");
        }
        if (basketIndex == null) {
            throw new Error("basketIndex is required");
        }
        const data = await ApiService.postData(
            `/home/jigsaw/${encodeURIComponent(jigsawId)}/color-baskets/unlock`,
            {
                appCode,
                basketCount: Number(basketCount),
                basketIndex: Number(basketIndex),
            },
        );
        const err = failureMessage(data, "Failed to unlock color basket");
        if (err) throw new Error(err);
        return {
            jigsawId: data?.jigsawId || jigsawId,
            basketCount: Number(data?.basketCount) || Number(basketCount),
            basketIndex: Number.isFinite(Number(data?.basketIndex))
                ? Number(data.basketIndex)
                : Number(basketIndex),
            unlocked: data?.unlocked !== false,
            coinsSpent: Number(data?.coinsSpent) || 0,
            totalPuzzleCoins: Number(data?.totalPuzzleCoins) || 0,
            unlockCoinCost: Number(data?.unlockCoinCost) || 0,
            appCode: data?.appCode || appCode,
        };
    },
};

export default HomePuzzleCoinsService;
